import React from 'react';

const metricConfig = {
  clinical: [
    { key: 'today_appointments', label: 'Citas hoy' },
    { key: 'patients', label: 'Pacientes activos' },
    { key: 'follow_up_pending', label: 'Seguimientos' },
    { key: 'low_stock_alerts', label: 'Alertas de stock' },
  ],
  content: [
    { key: 'scheduled_posts', label: 'Publicaciones programadas' },
    { key: 'pending_content', label: 'Backlog creativo' },
    { key: 'today_appointments', label: 'Citas hoy bloqueadas' },
    { key: 'patients', label: 'Datos clínicos protegidos' },
  ],
};

export const ClinicWorkspaceShell = ({ workspace, activeSection, onSectionChange, consultMode, onToggleConsultMode }) => {
  const isCommunity = workspace?.user?.role === 'community_manager';
  const cards = isCommunity ? metricConfig.content : metricConfig.clinical;
  const quickActions = isCommunity
    ? [
      { id: 'overview', label: 'Ver resumen' },
      { id: 'content', label: 'Abrir contenido' },
    ]
    : [
      { id: 'consult', label: 'Atender paciente' },
      { id: 'appointments', label: 'Ver agenda' },
      { id: 'patients', label: 'Buscar en CRM' },
      { id: 'histories', label: 'Abrir historial' },
    ];

  return (
    <section className="rounded-[32px] border border-white/60 bg-[#f7f4ec]/95 p-6 shadow-[0_30px_80px_rgba(15,23,42,0.16)]" data-testid="clinic-workspace-shell">
      <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
        <div className="max-w-3xl space-y-3">
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#0f766e]" data-testid="clinic-workspace-brand-name">
            {workspace.brand.name}
          </p>
          <div className="space-y-2">
            <h1 className="text-4xl font-black tracking-tight text-slate-900 sm:text-5xl" data-testid="clinic-workspace-heading">
              {isCommunity ? 'Hub de community manager' : 'Panel simple para el día a día'}
            </h1>
            <p className="max-w-2xl text-sm text-slate-600 sm:text-base" data-testid="clinic-workspace-tagline">
              {isCommunity ? workspace.brand.tagline : 'Todo en una sola vista: atender pacientes, escribir en la historia, revisar agenda, CRM y stock sin complicaciones.'}
            </p>
          </div>
        </div>

        <div className="space-y-3">
          {!isCommunity && (
            <button
              type="button"
              onClick={onToggleConsultMode}
              className={`w-full rounded-full px-5 py-3 text-sm font-semibold transition ${consultMode ? 'bg-slate-900 text-white' : 'bg-white text-slate-700'}`}
              data-testid="clinic-consult-mode-toggle"
            >
              {consultMode ? 'Modo consulta táctil activo' : 'Activar modo consulta táctil'}
            </button>
          )}
          <div className="rounded-[24px] border border-slate-200 bg-white/90 px-5 py-4 text-sm text-slate-600" data-testid="clinic-workspace-user-card">
          <p className="text-xs uppercase tracking-[0.24em] text-slate-400">Sesión activa</p>
          <p className="mt-2 text-lg font-semibold text-slate-900" data-testid="clinic-workspace-user-name">{workspace.user.nombre}</p>
          <p data-testid="clinic-workspace-user-role">{workspace.user.role_label}</p>
          <p className="mt-2 text-xs text-slate-500" data-testid="clinic-workspace-client-id">Cliente {workspace.client_identity?.client_id}</p>
          <p className="mt-2 text-xs text-[#0f766e]" data-testid="clinic-workspace-guardrail">{workspace.guardrail}</p>
          </div>
        </div>
      </div>

      <div className="mt-6 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {cards.map((card) => (
          <article
            key={card.key}
            className="rounded-[24px] border border-slate-200 bg-white px-5 py-4"
            data-testid={`clinic-metric-${card.key}`}
          >
            <p className="text-xs uppercase tracking-[0.22em] text-slate-400">{card.label}</p>
            <p className="mt-3 text-3xl font-black text-slate-900">{workspace.metrics?.[card.key] ?? 0}</p>
          </article>
        ))}
      </div>

      {!isCommunity && (
        <div className="mt-6 rounded-[24px] border border-slate-200 bg-white/90 p-5" data-testid="clinic-workspace-quick-actions-card">
          <p className="text-xs uppercase tracking-[0.24em] text-[#0f766e]">Lo más usado</p>
          <p className="mt-2 text-lg font-bold text-slate-900">Entradas rápidas para trabajar sin perder tiempo</p>
          <div className="mt-4 flex flex-wrap gap-3" data-testid="clinic-workspace-quick-actions-list">
            {quickActions.map((action) => (
              <button
                key={action.id}
                type="button"
                onClick={() => onSectionChange(action.id)}
                className="rounded-full bg-[#f8fafc] px-5 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-900 hover:text-white"
                data-testid={`clinic-workspace-quick-action-${action.id}`}
              >
                {action.label}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="mt-6 flex flex-wrap gap-3" data-testid="clinic-workspace-section-switcher">
        {workspace.sections.map((section) => {
          const active = activeSection === section.id;
          return (
            <button
              key={section.id}
              type="button"
              onClick={() => onSectionChange(section.id)}
              className={`rounded-full px-5 py-2 text-sm font-semibold transition ${active ? 'bg-slate-900 text-white' : 'bg-white text-slate-600 hover:bg-slate-100'}`}
              data-testid={`clinic-section-${section.id}-button`}
            >
              {section.label}
            </button>
          );
        })}
      </div>
    </section>
  );
};
